import InlinePopover from '@/components/InlinePopover';

// CoverageTip — Jul 2026, Coverage Watch column.
//
// Phone-first: the Screener table's native `title` hover tooltips are
// dead on touch, so both the Coverage CELL and the column HEADER ⓘ
// open an AbbrTip-style tap popup (InlinePopover → viewport-clamped,
// never overflows a 320px phone screen). Desktop users can click too.

const METRIC_EXPLAINERS = {
  FCF: 'Free Cash Flow (operating cash flow − capex) vs cash dividends paid — the corporate coverage standard.',
  FFO: 'Funds From Operations (net income + depreciation) vs dividends paid — the REIT coverage standard, since GAAP depreciation understates REIT cash earnings.',
  NII: 'Net Investment Income (after-expense interest, dividend & fee income) vs distributions paid — the BDC/CEF coverage standard.',
  DCF: 'Distributable Cash Flow (net income + depreciation) vs distributions paid — the MLP coverage standard.',
};

function metricKey(metric) {
  return (metric || 'FCF').split(' ')[0].toUpperCase();
}

export function coverageRatioClass(r) {
  if (r >= 1.2) return 'text-emerald-600 dark:text-emerald-400';
  if (r >= 1.0) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

/**
 * Tappable coverage pill for a screener row. Renders "0.64× FCF ↓33%"
 * and opens a rich per-ticker popup on tap/click.
 */
export function CoverageCellTip({ stock }) {
  const cov = stock?.sec_coverage;
  const r = Number(cov?.ratio_current);
  if (!cov || cov.status !== 'ok' || !Number.isFinite(r)) {
    return <span className="text-muted-foreground/50">—</span>;
  }
  const t = Number(cov.trend_pct);
  const hasTrend = Number.isFinite(t);
  const mKey = metricKey(cov.metric);
  const quarters = Array.isArray(cov.quarters) ? cov.quarters.slice(-5) : [];

  return (
    <InlinePopover
      maxWidth={300}
      ariaLabel={`SEC distribution coverage details for ${stock.ticker}`}
      testId={`coverage-tip-${stock.ticker}`}
      className="inline cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2 hover:decoration-foreground transition-colors"
      render={() => (
        <div className="text-xs leading-relaxed space-y-2 p-1">
          <div className="font-bold text-foreground">
            SEC Distribution Coverage — {stock.ticker}
          </div>
          <div className="font-mono text-sm">
            <span className={`font-extrabold ${coverageRatioClass(r)}`}>{r.toFixed(2)}×</span>
            <span className="text-muted-foreground ml-1.5">{mKey} · TTM</span>
            {hasTrend && (
              <span className={`ml-2 font-semibold ${t < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {t < 0 ? '\u2193' : '\u2191'}{Math.abs(t).toFixed(1)}% / ~4 qtrs
              </span>
            )}
          </div>
          <p className="text-foreground/85">{METRIC_EXPLAINERS[mKey] || METRIC_EXPLAINERS.FCF}</p>
          {quarters.length > 0 && (
            <div className="rounded-md bg-muted/40 px-2 py-1.5 space-y-0.5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                Per-quarter coverage
              </div>
              {quarters.map((q) => (
                <div key={q.end} className="flex justify-between font-mono text-[11px]">
                  <span className="text-muted-foreground">{q.end}</span>
                  <span className={coverageRatioClass(Number(q.ratio))}>
                    {Number(q.ratio).toFixed(2)}×
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="text-foreground/70">
            Below <span className="font-semibold">1.0×</span> = paying out more cash than
            generated. Sustained sub-1.0× coverage with a falling trend is the classic
            setup <em>before</em> a cut announcement.
          </p>
          <p className="text-[10px] text-muted-foreground">
            Source: SEC EDGAR XBRL filings
            {cov.latest_quarter_end ? ` · latest quarter ${cov.latest_quarter_end}` : ''}.
          </p>
        </div>
      )}
    >
      <span className="whitespace-nowrap">
        <span className={`font-bold ${coverageRatioClass(r)}`}>{r.toFixed(2)}×</span>
        <span className="text-muted-foreground ml-1">{mKey}</span>
        {hasTrend && (
          <span className={`ml-1 ${t < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {t < 0 ? '\u2193' : '\u2191'}{Math.abs(t).toFixed(0)}%
          </span>
        )}
      </span>
    </InlinePopover>
  );
}

/**
 * ⓘ info trigger for the Coverage column header. Sits INSIDE the
 * sortable <th>; taps on it must not trigger the sort, so the wrapper
 * stops propagation.
 */
export function CoverageHeaderTip() {
  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex align-middle ml-1">
      <InlinePopover
        maxWidth={290}
        ariaLabel="What is the Coverage column?"
        testId="coverage-header-tip"
        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-muted text-muted-foreground text-[9px] font-bold cursor-help hover:bg-muted-foreground/20 transition-colors"
        render={() => (
          <div className="text-xs leading-relaxed space-y-2 p-1">
            <div className="font-bold text-foreground">Coverage — SEC distribution coverage (TTM)</div>
            <p className="text-foreground/85">
              How many times the company's cash generation covered its dividend over the
              trailing twelve months, straight from SEC filings. The right yardstick per
              asset class: <span className="font-semibold">FCF</span> for corporates,{' '}
              <span className="font-semibold">FFO</span> for REITs,{' '}
              <span className="font-semibold">NII</span> for BDCs &amp; CEFs.
            </p>
            <p className="text-foreground/85">
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">≥1.2× safe cushion</span> ·{' '}
              <span className="text-amber-600 dark:text-amber-400 font-semibold">1.0–1.2× tight</span> ·{' '}
              <span className="text-rose-600 dark:text-rose-400 font-semibold">&lt;1.0× uncovered</span>.
              The arrow shows the ~4-quarter trend.
            </p>
            <p className="text-foreground/70">
              Tap the column title to sort — ascending puts the worst coverage (highest
              cut risk) first. Tap any row's coverage value for the per-quarter breakdown.
            </p>
          </div>
        )}
      >
        i
      </InlinePopover>
    </span>
  );
}
